import { Component } from '@theme/component';

class TestFreaksReviews extends Component {
  /**
   * The observer for lazy loading reviews
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadReviews();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Cached reviews data
   * @type {Object | null}
   */
  #reviewsData = null;

  /**
   * Current page offset
   * @type {number}
   */
  #currentOffset = 0;

  /**
   * Reviews per page (6 for 3x2 grid on desktop)
   * @type {number}
   */
  #reviewsPerPage = 6;

  /**
   * An abort controller for the active fetch
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Whether carousel scroll listener has been attached
   * @type {boolean}
   */
  #carouselScrollAttached = false;

  /**
   * Reviews from the initial fetch that exceed the per-page limit
   * @type {any[]}
   */
  #remainingReviews = [];

  /**
   * Pending next-page URLs, one per review source
   * @type {string[]}
   */
  #nextPageUrls = [];

  /**
   * Dedupe keys (id and content signature) of reviews already rendered or queued
   * @type {Set<string>}
   */
  #seenReviewKeys = new Set();

  /**
   * Image galleries for rendered reviews, indexed by data-gallery on each thumbnail
   * @type {any[][]}
   */
  #galleries = [];

  /**
   * The gallery and image currently shown in the lightbox
   * @type {{ gallery: any[], index: number } | null}
   */
  #lightboxState = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Setup event listeners for buttons
   */
  #setupEventListeners() {
    this.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');

      switch (action) {
        case 'write-review':
          this.#showIframe('write_review_url');
          break;
        case 'ask-question':
          this.#showIframe('ask_question_url');
          break;
        case 'back-to-reviews':
          this.#showReviews();
          break;
        case 'load-more':
          this.#loadMoreReviews();
          break;
        case 'lightbox-open':
          this.#openLightbox(
            Number(target.getAttribute('data-gallery')),
            Number(target.getAttribute('data-image-index'))
          );
          break;
        case 'lightbox-close':
          this.#closeLightbox();
          break;
        case 'lightbox-prev':
          this.#stepLightbox(-1);
          break;
        case 'lightbox-next':
          this.#stepLightbox(1);
          break;
      }
    });

    const lightbox = this.querySelector('[data-testid="reviews-lightbox"]');
    if (!(lightbox instanceof HTMLDialogElement)) return;

    // Clicking the backdrop (outside the dialog content) closes the lightbox
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) this.#closeLightbox();
    });

    lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.#stepLightbox(-1);
      if (e.key === 'ArrowRight') this.#stepLightbox(1);
    });

    lightbox.addEventListener('close', () => {
      this.#lightboxState = null;
    });
  }

  /**
   * Load reviews from TestFreaks API.
   * Queries by both the configured url key and the product title, then combines the results.
   */
  async #loadReviews() {
    const { testfreaksUrl, urlKey, productTitle } = this.dataset;

    if (!testfreaksUrl || !urlKey) {
      this.#showError('Missing TestFreaks configuration');
      return;
    }

    const keys = [...new Set([urlKey, productTitle].filter(Boolean))];

    try {
      /** @type {any[]} */
      const sources = [];
      for (const key of keys) {
        const onpageUrl = `https://js.testfreaks.com/onpage/${testfreaksUrl}/reviews.json?url_key=${encodeURIComponent(key)}`;
        const data = await this.#fetchJson(onpageUrl);
        if (data) sources.push(data);
      }

      if (sources.length === 0) {
        throw new Error('No TestFreaks data returned');
      }

      // Write review / ask question URLs come from the primary (url key) response
      this.#reviewsData = sources[0];

      const withReviews = sources.filter((data) => data.user_review_url || data.your_review_url);
      const summary = this.#combineSummaries(withReviews);

      if (withReviews.length > 0) {
        await this.#loadUserReviews(withReviews.map((data) => data.user_review_url || data.your_review_url));
        this.#renderSummary(summary);
        this.#updateStructuredData(summary);
      } else {
        this.#renderReviews([]);
        this.#renderPagination(false);
        this.#renderSummary(summary);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('TestFreaks API error:', error);
      this.#showError('Failed to load reviews');
    }
  }

  /**
   * Combine summary data from multiple sources, weighting score by review count
   * @param {any[]} sources
   * @returns {{ score: number, user_review_count: number }}
   */
  #combineSummaries(sources) {
    let totalCount = 0;
    let weightedScore = 0;

    for (const data of sources) {
      const count = Number(data?.user_review_count) || Number(data?.your_review_count) || 0;
      totalCount += count;
      weightedScore += (Number(data?.score) || 0) * count;
    }

    return {
      score: totalCount ? weightedScore / totalCount : 0,
      user_review_count: totalCount,
    };
  }

  /**
   * Load user reviews from one or more reviews endpoints
   * @param {string[]} urls
   */
  async #loadUserReviews(urls) {
    try {
      /** @type {any[]} */
      let allReviews = [];
      this.#nextPageUrls = [];

      for (const url of urls) {
        const data = await this.#fetchJson(url);
        if (!data) continue;

        allReviews = allReviews.concat(data.reviews || []);
        if (data.next_page_url) this.#nextPageUrls.push(data.next_page_url);
      }

      allReviews = this.#dedupeReviews(allReviews);

      const initialReviews = allReviews.slice(0, this.#reviewsPerPage);
      this.#remainingReviews = allReviews.slice(this.#reviewsPerPage);

      this.#renderReviews(initialReviews);
      this.#renderPagination(this.#remainingReviews.length > 0 || this.#nextPageUrls.length > 0);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('TestFreaks reviews error:', error);
      this.#showError('Failed to load user reviews');
    }
  }

  /**
   * Load more reviews (pagination)
   */
  async #loadMoreReviews() {
    while (this.#remainingReviews.length < this.#reviewsPerPage && this.#nextPageUrls.length > 0) {
      const nextPageUrl = this.#nextPageUrls.shift();
      if (!nextPageUrl) continue;

      try {
        const data = await this.#fetchJson(nextPageUrl);
        if (!data) continue;

        this.#remainingReviews = this.#dedupeReviews(this.#remainingReviews.concat(data.reviews || []));
        if (data.next_page_url) this.#nextPageUrls.push(data.next_page_url);
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    if (this.#remainingReviews.length === 0) {
      this.#renderPagination(false);
      return;
    }

    const batch = this.#remainingReviews.slice(0, this.#reviewsPerPage);
    this.#remainingReviews = this.#remainingReviews.slice(this.#reviewsPerPage);

    this.#renderReviews(batch, true);
    this.#renderPagination(this.#remainingReviews.length > 0 || this.#nextPageUrls.length > 0);
  }

  /**
   * Remove reviews already seen, matched by id or by identical content
   * (both url keys may resolve to the same product, or the same review may be syndicated to both)
   * @param {any[]} reviews
   * @returns {any[]}
   */
  #dedupeReviews(reviews) {
    return reviews.filter((review) => {
      const keys = [
        review?.id != null ? `id:${review.id}` : null,
        `content:${[review?.author, review?.date, review?.score, review?.score_max, review?.extract]
          .map((value) => String(value ?? '').trim().toLowerCase())
          .join('|')}`,
      ].filter(Boolean);

      if (keys.some((key) => this.#seenReviewKeys.has(key))) return false;
      for (const key of keys) this.#seenReviewKeys.add(key);
      return true;
    });
  }

  /**
   * Fetch a URL and parse JSON, returning null on a non-OK response
   * @param {string} url
   * @returns {Promise<any | null>}
   */
  async #fetchJson(url) {
    const response = await this.#fetchWithAbort(url);
    if (!response.ok) {
      console.warn(`TestFreaks request failed (${response.status}): ${url}`);
      return null;
    }
    return response.json();
  }

  /**
   * Render the reviews summary
   * @param {any} data
   */
  #renderSummary(data) {
    const summaryEl = this.querySelector('[data-testid="reviews-summary"]');
    if (!summaryEl) return;

    const scoreOutOf10 = Number(data?.score) || 0;
    const scoreOutOf5 = scoreOutOf10 / 2;
    const reviewCount = Number(data?.user_review_count) || Number(data?.your_review_count) || 0;

    summaryEl.innerHTML = `
      <div class="testfreaks-reviews__overall">
        <div class="testfreaks-reviews__overall-score">${scoreOutOf5.toFixed(1)}</div>
        <div class="testfreaks-reviews__overall-details">
          ${this.#renderStars(scoreOutOf5, 5)}
          <p class="testfreaks-reviews__overall-count">
            Based on ${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Render reviews list
   * @param {any[]} reviews
   * @param {boolean} append
   */
  #renderReviews(reviews, append = false) {
    const listEl = this.querySelector('[data-testid="reviews-list"]');
    if (!listEl) return;

    if (!append) {
      listEl.innerHTML = '';
      this.#galleries = [];
    }

    if (!Array.isArray(reviews) || reviews.length === 0) {
      listEl.innerHTML = '<p class="testfreaks-reviews__empty">No reviews yet. Be the first to review!</p>';
      return;
    }

    const reviewsHtml = reviews
      .map((review) => this.#renderReviewItem(review))
      .join('');

    if (append) {
      listEl.insertAdjacentHTML('beforeend', reviewsHtml);
    } else {
      listEl.innerHTML = reviewsHtml;
    }

    this.#renderCarouselIndicators();
  }

  /**
   * Render carousel progress indicators for mobile
   */
  #renderCarouselIndicators() {
    const listEl = this.querySelector('[data-testid="reviews-list"]');
    const indicatorsEl = this.querySelector('[data-testid="carousel-indicators"]');
    if (!listEl || !indicatorsEl) return;

    const items = listEl.querySelectorAll('.testfreaks-reviews__item');
    const count = items.length;
    if (count === 0) return;

    const dots = Array.from({ length: count }, (_v, i) =>
      `<span class="testfreaks-reviews__indicator${i === 0 ? ' testfreaks-reviews__indicator--active' : ''}"></span>`
    ).join('');

    indicatorsEl.innerHTML = dots;

    this.#setupCarouselScroll(listEl, indicatorsEl);
  }

  /**
   * Setup scroll listener for carousel indicator updates
   * @param {Element} listEl
   * @param {Element} indicatorsEl
   */
  #setupCarouselScroll(listEl, indicatorsEl) {
    if (this.#carouselScrollAttached) return;
    this.#carouselScrollAttached = true;

    let scrollTimeout = null;

    listEl.addEventListener('scroll', () => {
      if (scrollTimeout) cancelAnimationFrame(scrollTimeout);

      scrollTimeout = requestAnimationFrame(() => {
        const scrollLeft = listEl.scrollLeft;
        const itemWidth = listEl.querySelector('.testfreaks-reviews__item')?.offsetWidth || 1;
        const gap = 24;
        const activeIndex = Math.round(scrollLeft / (itemWidth + gap));
        const currentIndicators = this.querySelector('[data-testid="carousel-indicators"]');
        if (!currentIndicators) return;

        const indicators = currentIndicators.querySelectorAll('.testfreaks-reviews__indicator');

        for (const indicator of indicators) {
          indicator.classList.remove('testfreaks-reviews__indicator--active');
        }

        if (indicators[activeIndex]) {
          indicators[activeIndex].classList.add('testfreaks-reviews__indicator--active');
        }
      });
    }, { passive: true });
  }

  /**
   * Render a single review item
   * @param {any} review
   * @returns {string}
   */
  #renderReviewItem(review) {
    const reviewDate = review?.date ? new Date(review.date) : new Date();
    const date = reviewDate.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const author = review?.author || '';
    const score = Number(review?.score) || 0;
    const scoreMax = Number(review?.score_max) || 10;
    const extract = review?.extract || '';
    const source = review?.source || '';
    const logo = review?.logo || '';

    return `
      <div class="testfreaks-reviews__item">
        ${this.#renderAvatar(author)}
        <div class="testfreaks-reviews__item-body">
          ${author ? `<h4 class="testfreaks-reviews__item-author">${this.#escapeHtml(author)}</h4>` : ''}
          <div class="testfreaks-reviews__item-rating-date">
            ${this.#renderStars(score, scoreMax)}
            <span class="testfreaks-reviews__item-date">${date}</span>
          </div>
          ${extract ? `<p class="testfreaks-reviews__item-content">${this.#escapeHtml(extract)}</p>` : ''}
          ${this.#renderProsCons(review)}
          ${this.#renderImages(review)}
          ${source || logo ? this.#renderSource(review) : ''}
        </div>
      </div>
    `;
  }

  /**
   * Render an initials-based avatar
   * @param {string} name
   * @returns {string}
   */
  #renderAvatar(name) {
    const initials = name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);

    return `
      <div class="testfreaks-reviews__avatar" aria-hidden="true">
        <span class="testfreaks-reviews__avatar-initials">${this.#escapeHtml(initials || '?')}</span>
      </div>
    `;
  }

  /**
   * Render pros and cons lists (each provided as a comma-separated string)
   * @param {any} review
   * @returns {string}
   */
  #renderProsCons(review) {
    const pros = this.#splitPoints(review?.pros);
    const cons = this.#splitPoints(review?.cons);
    if (pros.length === 0 && cons.length === 0) return '';

    /**
     * @param {string[]} points
     * @param {string} modifier
     * @param {string} heading
     */
    const renderList = (points, modifier, heading) => {
      if (points.length === 0) return '';

      return `
        <div class="testfreaks-reviews__item-points-group">
          <p class="testfreaks-reviews__item-points-heading">${this.#escapeHtml(heading)}</p>
          <ul class="testfreaks-reviews__item-points testfreaks-reviews__item-points--${modifier}">
            ${points.map((point) => `<li class="testfreaks-reviews__item-point">${this.#escapeHtml(point)}</li>`).join('')}
          </ul>
        </div>
      `;
    };

    return `
      <div class="testfreaks-reviews__item-pros-cons">
        ${renderList(pros, 'pros', this.dataset.prosText || 'Pros')}
        ${renderList(cons, 'cons', this.dataset.consText || 'Cons')}
      </div>
    `;
  }

  /**
   * Split a comma-separated string into trimmed, non-empty points
   * @param {unknown} value
   * @returns {string[]}
   */
  #splitPoints(value) {
    if (typeof value !== 'string') return [];

    return value
      .split(',')
      .map((point) => point.trim())
      .filter(Boolean);
  }

  /**
   * Render review image thumbnails that open in the lightbox
   * @param {any} review
   * @returns {string}
   */
  #renderImages(review) {
    const images = Array.isArray(review?.images)
      ? review.images.filter((image) => image?.icon && (image.medium || image.large))
      : [];
    if (images.length === 0) return '';

    const galleryIndex = this.#galleries.push(images) - 1;
    const count = images.length;

    const thumbnails = images
      .map((image, i) => {
        const label = this.#formatCount(this.dataset.viewImageText || 'View image [index] of [count]', i + 1, count);

        return `
          <li>
            <button
              type="button"
              class="testfreaks-reviews__item-image-button"
              data-action="lightbox-open"
              data-gallery="${galleryIndex}"
              data-image-index="${i}"
              aria-label="${this.#escapeHtml(label)}"
              aria-haspopup="dialog"
            >
              <img
                src="${this.#escapeHtml(image.icon)}"
                alt=""
                class="testfreaks-reviews__item-image"
                width="${Number(image.icon_width) || 100}"
                height="${Number(image.icon_height) || 100}"
                loading="lazy"
              >
            </button>
          </li>
        `;
      })
      .join('');

    return `
      <ul class="testfreaks-reviews__item-images" aria-label="${this.#escapeHtml(this.dataset.reviewImagesText || 'Review images')}">
        ${thumbnails}
      </ul>
    `;
  }

  /**
   * Open the lightbox at an image within a review's gallery
   * @param {number} galleryIndex
   * @param {number} imageIndex
   */
  #openLightbox(galleryIndex, imageIndex) {
    const lightbox = this.querySelector('[data-testid="reviews-lightbox"]');
    const gallery = this.#galleries[galleryIndex];
    if (!(lightbox instanceof HTMLDialogElement) || !gallery) return;

    this.#lightboxState = { gallery, index: imageIndex };
    this.#renderLightboxImage();

    if (!lightbox.open) lightbox.showModal();
  }

  /**
   * Close the lightbox
   */
  #closeLightbox() {
    const lightbox = this.querySelector('[data-testid="reviews-lightbox"]');
    if (lightbox instanceof HTMLDialogElement && lightbox.open) lightbox.close();
  }

  /**
   * Move to the previous or next image in the lightbox, wrapping around
   * @param {number} direction - -1 for previous, 1 for next
   */
  #stepLightbox(direction) {
    if (!this.#lightboxState) return;

    const { gallery, index } = this.#lightboxState;
    if (gallery.length < 2) return;

    this.#lightboxState.index = (index + direction + gallery.length) % gallery.length;
    this.#renderLightboxImage();
  }

  /**
   * Render the current lightbox image, status and navigation visibility
   */
  #renderLightboxImage() {
    if (!this.#lightboxState) return;

    const { gallery, index } = this.#lightboxState;
    const image = gallery[index];
    const figureEl = this.querySelector('[data-testid="lightbox-figure"]');
    const statusEl = this.querySelector('[data-testid="lightbox-status"]');
    if (!figureEl || !image) return;

    let imageEl = figureEl.querySelector('[data-testid="lightbox-image"]');
    if (!(imageEl instanceof HTMLImageElement)) {
      imageEl = document.createElement('img');
      imageEl.className = 'testfreaks-reviews__lightbox-image';
      imageEl.dataset.testid = 'lightbox-image';
      figureEl.prepend(imageEl);
    }

    const count = gallery.length;
    const src = image.medium || image.large;

    imageEl.src = src;
    imageEl.width = Number(image.medium ? image.medium_width : image.large_width) || 0;
    imageEl.height = Number(image.medium ? image.medium_height : image.large_height) || 0;
    if (image.large && image.medium) {
      imageEl.srcset = `${image.medium} ${image.medium_width}w, ${image.large} ${image.large_width}w`;
      imageEl.sizes = 'min(100vw - 2rem, 1200px)';
    } else {
      imageEl.removeAttribute('srcset');
      imageEl.removeAttribute('sizes');
    }
    imageEl.alt = this.#formatCount(this.dataset.imageAltText || 'Customer review image [index] of [count]', index + 1, count);

    if (statusEl) statusEl.textContent = count > 1 ? `${index + 1} / ${count}` : '';

    for (const button of this.querySelectorAll('[data-action="lightbox-prev"], [data-action="lightbox-next"]')) {
      if (button instanceof HTMLElement) button.hidden = count < 2;
    }
  }

  /**
   * Replace [index] and [count] placeholders in a translated string
   * @param {string} template
   * @param {number} index
   * @param {number} count
   * @returns {string}
   */
  #formatCount(template, index, count) {
    return template.replace('[index]', String(index)).replace('[count]', String(count));
  }

  /**
   * Render review source information
   * @param {any} review
   * @returns {string}
   */
  #renderSource(review) {
    const logo = review?.logo || '';
    const source = review?.source || '';
    
    return `
      <div class="testfreaks-reviews__item-source">
        ${logo ? `<img src="${logo}" alt="${this.#escapeHtml(source || 'Review source')}" class="testfreaks-reviews__item-source-logo" loading="lazy">` : ''}
        ${source ? `<span class="testfreaks-reviews__item-source-text">via ${this.#escapeHtml(source)}</span>` : ''}
      </div>
    `;
  }

  /**
   * Render star rating
   * @param {number} score - Raw score (may be out of 5 or 10)
   * @param {number} maxScore - Max score (5 or 10)
   * @returns {string}
   */
  #renderStars(score, maxScore = 5) {
    // Normalize score to 5-star scale
    const normalizedScore = maxScore === 10 ? score / 2 : score;
    const fullStars = Math.floor(normalizedScore);
    const decimal = normalizedScore % 1;
    
    const hasPartialStar = decimal > 0;
    const partialStarIndex = fullStars + 1;

    const starPath = 'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

    const starSvg = (index) => {
      const isPartial = hasPartialStar && index === partialStarIndex;
      const isFilled = index <= fullStars;
      const uniqueId = `partial-star-testfreaks-${index}-${Date.now()}`;
      
      if (isPartial) {
        // Convert decimal to percentage for gradient
        const percentage = Math.round(decimal * 100);
        return `<svg class="testfreaks-reviews__star partial-star" viewBox="0 0 32 32" role="presentation" aria-hidden="true">
          <defs>
            <linearGradient id="${uniqueId}" x1="0" x2="100%" y1="0" y2="0">
              <stop offset="${percentage}%" stop-color="var(--star-fill-color)" stop-opacity="1"></stop>
              <stop offset="${percentage}%" stop-color="var(--star-fill-color)" stop-opacity="0.2"></stop>
            </linearGradient>
          </defs>
          <path fill="url(#${uniqueId})" d="${starPath}" />
        </svg>`;
      }
      
      return `<svg class="testfreaks-reviews__star ${isFilled ? 'filled' : 'empty'}" viewBox="0 0 32 32" role="presentation" aria-hidden="true">
        <path d="${starPath}" />
      </svg>`;
    };

    const stars = Array.from({ length: 5 }, (_v, i) => starSvg(i + 1)).join('');

    return `
      <div class="testfreaks-reviews__stars" aria-label="Rating: ${normalizedScore.toFixed(1)} out of 5 stars">
        ${stars}
      </div>
    `;
  }

  /**
   * Render pagination controls
   * @param {boolean} hasMore
   */
  #renderPagination(hasMore) {
    const paginationEl = this.querySelector('[data-testid="reviews-pagination"]');
    if (!paginationEl) return;

    if (hasMore) {
      paginationEl.innerHTML = `
        <button
          type="button"
          class="button button--secondary"
          data-action="load-more"
        >
          Load More Reviews
        </button>
      `;
    } else {
      paginationEl.innerHTML = '';
    }
  }

  /**
   * Show iframe content
   * @param {string} urlKey
   */
  #showIframe(urlKey) {
    if (!this.#reviewsData || typeof this.#reviewsData !== 'object') {
      console.error('Reviews data not loaded');
      return;
    }

    const url = this.#reviewsData[urlKey];
    if (!url) {
      console.error(`URL not found: ${urlKey}`);
      return;
    }

    const contentArea = this.querySelector('[data-testid="content-area"]');
    const iframeContainer = this.querySelector('[data-testid="iframe-container"]');
    const iframe = this.querySelector('[data-testid="reviews-iframe"]');
    const actionsEl = this.querySelector('[data-testid="reviews-actions"]');

    if (!contentArea || !iframeContainer || !iframe || !actionsEl) return;
    if (!(iframe instanceof HTMLIFrameElement)) return;

    contentArea.hidden = true;
    iframe.src = url;
    iframeContainer.hidden = false;
    
    actionsEl.innerHTML = `
      <button
        type="button"
        class="button button--secondary"
        data-action="back-to-reviews"
      >
        Back to Reviews
      </button>
    `;
  }

  /**
   * Show reviews list
   */
  #showReviews() {
    const contentArea = this.querySelector('[data-testid="content-area"]');
    const iframeContainer = this.querySelector('[data-testid="iframe-container"]');
    const iframe = this.querySelector('[data-testid="reviews-iframe"]');
    const actionsEl = this.querySelector('[data-testid="reviews-actions"]');

    if (!contentArea || !iframeContainer || !iframe || !actionsEl) return;
    if (!(iframe instanceof HTMLIFrameElement)) return;

    iframeContainer.hidden = true;
    iframe.src = '';
    contentArea.hidden = false;
    
    const writeReviewText = this.dataset.writeReviewText || 'Write a Review';
    const askQuestionText = this.dataset.askQuestionText || 'Ask a Question';
    
    actionsEl.innerHTML = `
      <button
        type="button"
        class="button button--secondary testfreaks-reviews__action-button"
        data-action="write-review"
      >
        ${writeReviewText}
      </button>
      <button
        type="button"
        class="button button--secondary testfreaks-reviews__action-button"
        data-action="ask-question"
      >
        ${askQuestionText}
      </button>
    `;
  }

  /**
   * Show error message
   * @param {string} message
   */
  #showError(message) {
    const summaryEl = this.querySelector('[data-testid="reviews-summary"]');
    const listEl = this.querySelector('[data-testid="reviews-list"]');

    if (summaryEl) {
      summaryEl.innerHTML = `<p class="testfreaks-reviews__error">${message}</p>`;
    }

    if (listEl) {
      listEl.innerHTML = '';
    }
  }

  /**
   * Fetch with abort controller
   * @param {string} url
   * @returns {Promise<Response>}
   */
  async #fetchWithAbort(url) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      return await fetch(url, { signal: this.#activeFetch.signal });
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Update structured data with TestFreaks reviews
   * @param {any} data
   */
  #updateStructuredData(data) {
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (!existingScript) return;

    try {
      const structuredData = JSON.parse(existingScript.textContent || '{}');
      
      const scoreOutOf10 = Number(data?.score) || 0;
      const scoreOutOf5 = scoreOutOf10 / 2;
      const reviewCount = Number(data?.user_review_count) || Number(data?.your_review_count) || 0;

      if (structuredData['@type'] === 'Product') {
        structuredData.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: scoreOutOf5.toFixed(1),
          reviewCount: reviewCount,
          bestRating: '5',
          worstRating: '1',
        };

        existingScript.textContent = JSON.stringify(structuredData, null, 2);
      }
    } catch (error) {
      console.error('Failed to update structured data:', error);
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/"/g, '&quot;');
  }
}

if (!customElements.get('testfreaks-reviews')) {
  customElements.define('testfreaks-reviews', TestFreaksReviews);
}
